import React, { useState, createContext, useEffect } from 'react'

import { QueryClient } from '@tanstack/react-query'
import { TRACK_CONTENT_TYPES, TrackContentType } from '@mc/track-event'

import { Course } from 'types/data'

import { PageType, SampleProps, TrailerProps } from './types'

interface AboutSectionVideoProps {
  series: Course
  pageType: PageType
  trailer?: TrailerProps
  sample?: SampleProps
  children: React.ReactNode
}

export interface AboutSectionVideoContextValue {
  videoId?: string
  mediaUuid?: string
  isPlaying: boolean
  hasPlayed: boolean
  handleVideoPlay: () => void
  handleVideoPause: () => void
  toggleVideo: (id?: string, mUuid?: string) => void
  isSample: boolean
  trackingParams: {
    contentId?: string
    contentTitle: string
    contentType: TrackContentType
    courseId: string | number
  }
  pageType: PageType
}

const getTrackingProps = (
  series: Course,
  videoId?: string,
  mediaUuid?: string,
) => {
  if (series.brightcoveVideoId) {
    return series.brightcoveVideoId === videoId
      ? {
          contentId: `${series.id}_${series.brightcoveVideoId}`,
          contentTitle: `${series.title} Trailer`,
          contentType: TRACK_CONTENT_TYPES.COURSE_TRAILER,
          courseId: series.id,
        }
      : {
          contentId: `${series.id}_${series.sample?.brightcoveVideoId}`,
          contentTitle: `${series.title} Preview`,
          contentType: TRACK_CONTENT_TYPES.COURSE_PREVIEW,
          courseId: series.id,
        }
  }
  if (series.mediaUuid) {
    return series.mediaUuid === mediaUuid
      ? {
          contentId: `${series.id}_${series.mediaUuid}`,
          contentTitle: `${series.title} Trailer`,
          contentType: TRACK_CONTENT_TYPES.COURSE_TRAILER,
          courseId: series.id,
        }
      : {
          contentId: `${series.id}_${series.sample?.mediaUuid}`,
          contentTitle: `${series.title} Preview`,
          contentType: TRACK_CONTENT_TYPES.COURSE_PREVIEW,
          courseId: series.id,
        }
  }

  return {
    contentTitle: `${series.title} Unknown`,
    contentType: TRACK_CONTENT_TYPES.COURSE_PREVIEW,
    courseId: series.id,
  }
}

export const queryClient = new QueryClient()

const AboutSectionVideoContext =
  createContext<AboutSectionVideoContextValue | null>(null)

export const AboutSectionVideoContextProvider = ({
  series,
  trailer,
  sample,
  children,
  pageType,
}: AboutSectionVideoProps) => {
  const [videoId, setVideoId] = useState(
    trailer?.brightcoveVideoId || sample?.brightcoveVideoId,
  )
  const [mediaUuid, setMediaUuid] = useState(
    trailer?.mediaUuid || sample?.mediaUuid,
  )
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasPlayed, setHasPlayed] = useState(false)

  useEffect(() => {
    if (isPlaying) setHasPlayed(true)
  }, [isPlaying])

  const handleVideoPlay = () => {
    setIsPlaying(true)
  }

  const handleVideoPause = () => {
    setIsPlaying(false)
  }

  const toggleVideo = (id?: string, mUuid?: string) => {
    setVideoId(id)
    setMediaUuid(mUuid)
    if (videoId === id || mUuid === mediaUuid) {
      setIsPlaying((prevPlaying) => !prevPlaying)
    } else {
      setIsPlaying(true)
    }
  }

  const isSample =
    videoId !== trailer?.brightcoveVideoId || mediaUuid !== trailer?.mediaUuid

  const trackingParams = getTrackingProps(series, videoId, mediaUuid)

  return (
    <AboutSectionVideoContext.Provider
      value={{
        videoId,
        mediaUuid,
        isPlaying,
        hasPlayed,
        handleVideoPlay,
        handleVideoPause,
        toggleVideo,
        isSample,
        trackingParams,
        pageType,
      }}
    >
      {children}
    </AboutSectionVideoContext.Provider>
  )
}

export const useAboutSectionVideo = () => {
  const ctx = React.useContext(AboutSectionVideoContext)
  if (!ctx) {
    throw new Error(
      'AboutSectionVideoContext cannot be used outside of AboutSectionVideoContextProvider',
    )
  }
  return ctx
}
